import React, { type JSX } from 'react';

import {
  type RadioGroupProps,
  type TextFieldProps,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  styled,
} from '@mui/material';

import { Show } from '../../../components/Show';
import { getIsOptionEligibleForComment } from '../../parsing/parser';
import { ErrorMessageText } from './common';

type RadioGroupingValue = { value: RadioValue; comment?: string };
type RadioValue = string | number;

type RadioGroupingProps = {
  options: { label: string; value: RadioValue; commentEligibility?: string }[];
  name: string;
  defaultValue?: RadioGroupingValue;
  value: RadioGroupingValue;
  onChange: (value: RadioGroupingValue) => void;
  error?: string;
  disabled?: boolean;
};

export const RadioGrouping = ({
  options,
  name,
  defaultValue,
  value,
  onChange,
  error,
  disabled,
}: RadioGroupingProps) => {
  const handleRadioChange: RadioGroupProps['onChange'] = (
    e,
    nextRadioValue
  ) => {
    onChange?.({ ...value, value: nextRadioValue });
  };

  const handleCommentChange: TextFieldProps['onChange'] = (e) => {
    onChange?.({ ...value, comment: e.target.value });
  };

  const isCommentEligibleForSelection = options
    .filter(getIsOptionEligibleForComment)
    .some((opt) => opt.value == value.value);

  return (
    <FormControl variant="standard" component="fieldset" error={Boolean(error)}>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <RadioGroup
          name={name}
          sx={{ flexDirection: 'row', flexWrap: 'nowrap' }}
          defaultValue={defaultValue}
          value={value}
          onChange={handleRadioChange}
        >
          {options.map((option) => {
            const isOptionEligibleForComment =
              getIsOptionEligibleForComment(option);

            return (
              <React.Fragment key={option.value}>
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio
                      color="secondary"
                      checked={value.value == option.value}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {option.label}
                      <Show when={isOptionEligibleForComment}>
                        <StyledTextField
                          variant="standard"
                          onChange={handleCommentChange}
                          required
                          multiline
                          value={value.comment}
                          disabled={!isCommentEligibleForSelection || disabled}
                          placeholder="Describe"
                        />
                      </Show>
                    </Box>
                  }
                />
              </React.Fragment>
            );
          })}
        </RadioGroup>
      </Box>

      <ErrorMessageText>{error}</ErrorMessageText>
    </FormControl>
  );
};

type RadioGroupingContainerProps = {
  labelEl: JSX.Element | null;
} & RadioGroupingProps;

export const RadioGroupingContainer = (props: RadioGroupingContainerProps) => {
  if (props.options?.length >= 5) {
    return (
      <React.Fragment>
        {props.labelEl}

        <RadioGrouping {...props} />
      </React.Fragment>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
    >
      {props.labelEl}
      <RadioGrouping {...props} />
    </Box>
  );
};

const StyledTextField = styled(TextField)`
  margin: auto 0;

  & .MuiInputBase-input {
    padding: 2px 0 2px 2px;

    background: transparent;
    border: none;
    border-bottom: ${(props) =>
      `1px solid ${props.theme.palette.text.contrastText}`};
    border-radius: 0;

    height: 0.15rem;

    &.Mui-disabled {
      background: transparent;
    }
  }
`;
